import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  FormDialog,
  MuiBox,
  MuiTable,
  SvgIcon,
  SvgIconButton,
  Tooltip,
  Typography
} from '@kis-technologies/kis-ui';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { Area, actions as areasActions, selectors as areasSelectors } from 'store/areas';
import { getSortedList, getUniqueName } from 'utils/stringFormat';
import { AreaFormData, AreaUpdateFormData } from '../../../types';
import { useKioskFormStyles } from '../styled';
import { AreaForm } from './AreaForm';

interface Props {
  venueId: string;
  onClose: () => void;
}

export const KioskAreaOverlay: React.FC<Props> = ({ venueId, onClose }) => {
  const classes = useKioskFormStyles();

  const [getAreas, deleteArea, createArea, updateArea, resetError] = useActions([
    areasActions.getAreas,
    areasActions.deleteArea,
    areasActions.createArea,
    areasActions.updateArea,
    areasActions.resetError
  ]);

  const areas = useSelector(areasSelectors.selectAreas);
  const createdArea = useSelector(areasSelectors.selectCreatedArea);
  const filteredAreas = areas.filter((area) => area.venueId === venueId);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);
  const [showCreateEdit, setShowCreateEdit] = useState(false);
  const [showDeleteOverlay, setShowDeleteOverlay] = useState(false);
  const [sortColumn, setSortColumn] = useState<'name' | 'description'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [uiLoading, setUILoading] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  const { error, pending } = useSelector(areasSelectors.selectAction);

  const sortedAreas = useMemo(() => {
    if (!filteredAreas?.length) return [];

    const sorted = getSortedList(filteredAreas, sortColumn);
    return sortOrder === 'asc' ? sorted : [...sorted].reverse();
  }, [filteredAreas, sortColumn, sortOrder]);

  useEffect(() => {
    getAreas();
  }, [getAreas]);

  useEffect(() => {
    if (uiLoading && !pending) {
      if (!error) {
        setShowCreateEdit(false);
        setSelectedArea(null);
        getAreas();
        // A finished copy opens the new area for editing
        if (isCopying && createdArea) {
          setSelectedArea(createdArea);
          setShowCreateEdit(true);
        }
      }
      setIsCopying(false);
      setUILoading(false);
    }
  }, [uiLoading, pending, error, getAreas, isCopying, createdArea]);

  // The form stays open until the save finishes, so an error keeps what was typed
  const handleAreaSubmit = (data: AreaFormData) => {
    setUILoading(true);

    if (selectedArea) {
      updateArea({
        id: selectedArea.id,
        ...data
      });
    } else {
      createArea({
        ...data,
        venueId
      });
    }
  };

  /**
   * EDIT VENUE
   */
  const handleEdit = (venue: Area) => {
    setSelectedArea(venue);
    setShowCreateEdit(true);
  };

  /**
   * COPY AREA
   */
  const handleCopy = (area: Area) => {
    setUILoading(true);
    setIsCopying(true);
    createArea({
      name: getUniqueName(area.name, areas),
      description: area.description ? area.description : undefined,
      venueId: area.venueId
    });
  };

  /**
   * DELETE VENUE
   */
  const handleDelete = () => {
    if (!selectedArea) return;

    setUILoading(true);
    deleteArea({ id: selectedArea.id });

    setSelectedArea(null);
    setShowDeleteOverlay(false);
  };

  /**
   * FORM CLOSE
   */
  const handleOnFormClose = () => {
    setShowCreateEdit(false);
    setSelectedArea(null);

    getAreas();
  };

  /**
   * INITIAL DATA FOR EDIT
   */
  const getInitialData = (): AreaUpdateFormData | undefined => {
    if (!selectedArea) return undefined;

    return {
      id: selectedArea.id,
      name: selectedArea.name,
      description: selectedArea.description
    };
  };

  const handleSort = (column: 'name' | 'description') => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const SortArrow = ({ active }: { active: boolean }) =>
    active ? (
      <SvgIcon name={sortOrder === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: '5px' }} />
    ) : null;

  return (
    <>
      <FormDialog
        open
        title="Areas"
        confirmButtonText="Close"
        confirmButtonAction={onClose}
        headerButtonAction={() => {
          setSelectedArea(null);
          setShowCreateEdit(true);
        }}
        headerButtonText="Create Area"
      >
        <MuiBox mb="1rem" mt="14px">
          <Card hasBorderRadius raised fullHeight>
            <Box flexOverride="1">
              <MuiTable.TableContainer id="kioskVenueTable">
                <MuiTable.Table stickyHeader>
                  <MuiTable.TableHead>
                    <MuiTable.TableRow>
                      <MuiTable.TableCell
                        className={classes.kioskDetailsHeaders}
                        sx={{ width: '220px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                        onClick={() => handleSort('name')}
                      >
                        Name <SortArrow active={sortColumn === 'name'} />
                      </MuiTable.TableCell>

                      <MuiTable.TableCell
                        className={classes.kioskDetailsHeaders}
                        sx={{ width: '220px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                        onClick={() => handleSort('description')}
                      >
                        Description <SortArrow active={sortColumn === 'description'} />
                      </MuiTable.TableCell>

                      <MuiTable.TableCell
                        className={classes.kioskDetailsHeaders}
                        sx={{ cursor: 'default', textAlign: 'center' }}
                      >
                        Actions
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  </MuiTable.TableHead>

                  <MuiTable.TableBody>
                    {sortedAreas.map((area: Area) => (
                      <MuiTable.TableRow key={area.id}>
                        <MuiTable.TableCell>{area.name}</MuiTable.TableCell>

                        <MuiTable.TableCell>{area.description}</MuiTable.TableCell>

                        <MuiTable.TableCell align="center">
                          <MuiBox display="flex" gap="10px">
                            <Tooltip title="Edit Area" arrow placement="top" verticalOffset={-8}>
                              <SvgIconButton onClick={() => handleEdit(area)}>
                                <SvgIcon name="edit_circle" />
                              </SvgIconButton>
                            </Tooltip>

                            <Tooltip title="Copy Area" arrow placement="top" verticalOffset={-8}>
                              <SvgIconButton
                                onClick={() => handleCopy(area)}
                                disabled={uiLoading || pending}
                                data-testid="copy-area-button"
                              >
                                <SvgIcon name="copy_circle" />
                              </SvgIconButton>
                            </Tooltip>

                            <Tooltip title="Delete Area" arrow placement="top" verticalOffset={-8}>
                              <SvgIconButton
                                data-testid="delete-area-button"
                                onClick={() => {
                                  setSelectedArea(area);
                                  setShowDeleteOverlay(true);
                                }}
                              >
                                <SvgIcon name="exit_circle" />
                              </SvgIconButton>
                            </Tooltip>
                          </MuiBox>
                        </MuiTable.TableCell>
                      </MuiTable.TableRow>
                    ))}
                  </MuiTable.TableBody>
                </MuiTable.Table>
              </MuiTable.TableContainer>
            </Box>
          </Card>

          {/* DELETE CONFIRMATION */}
          {showDeleteOverlay && (
            <FormDialog
              open={showDeleteOverlay}
              confirmButtonAction={handleDelete}
              confirmButtonText="Delete"
              cancelButtonAction={() => {
                setSelectedArea(null);
                setShowDeleteOverlay(false);
              }}
              cancelButtonText="Cancel"
            >
              <MuiBox>
                <Typography fontSize={24}>Are you sure you want to delete this area?</Typography>
              </MuiBox>
            </FormDialog>
          )}

          {/* CREATE / EDIT FORM */}
          {showCreateEdit && (
            <AreaForm
              open={showCreateEdit}
              mode={selectedArea ? 'edit' : 'create'}
              initialData={getInitialData()}
              onClose={handleOnFormClose}
              onSubmit={handleAreaSubmit}
            />
          )}
        </MuiBox>

        {error && (
          <FormDialog cancelButtonAction={resetError} confirmButtonAction={resetError} confirmButtonText="Ok" open>
            <Typography fontSize={24} maxWidth="524px">
              {error}
            </Typography>
          </FormDialog>
        )}
      </FormDialog>
    </>
  );
};
